import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';
import { IMPERSONATE_ADMIN_ID, KEY_SESSION_UUID, KEY_USER_UUID } from '../constants';

const UNKNOWN_HTTP_VALUE = 'unknown';
const HTTP_TOO_MANY_REQUESTS_STATUS = 429;
const WRONG_OTP_DETAIL = 'Wrong OTP';
const NUMERIC_PATH_SEGMENT_PATTERN = /^\d+$/;
const UUID_PATH_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface AxiosErrorSentryContext {
  endpoint: string;
  method: string;
  statusCode: string;
}

interface AxiosErrorLike {
  code?: unknown;
  config?: {
    method?: string;
    url?: string;
  };
  isAxiosError?: boolean;
  message?: unknown;
  name?: unknown;
  response?: {
    data?: {
      detail?: unknown;
    };
    status?: number;
  };
}

const SENTRY_WEBVIEW_JUNK_PATTERNS: RegExp[] = [
  /domInteractive/i,
  /xbrowser/i,
  /swbrowser/i,
  /Java object is gone/i,
  /Error invoking postMessage/i,
  /Error invoking enableDidUserTypeOnKeyboardLogging/i,
  /webkit\.messageHandlers/i,
  /navigationPerformanceLoggerWithReply/i,
];

const SENTRY_CLIENT_NETWORK_NOISE_PATTERNS: RegExp[] = [
  /AxiosError:\s*Network Error/i,
  /Failed to load the JS script of the agent/i,
];
const SENTRY_HTTP_429_PATTERNS: RegExp[] = [/status code 429/i, /\[429\]/, /Too Many Requests/i];
const OMNI_WIDGET_FILE_PATTERN = /\/omni-widget\/WtOmniWidget\.umd\.js/i;
const OMNI_WIDGET_AUDIO_NOT_ALLOWED_PATTERN =
  /NotAllowedError.*The request is not allowed by the user agent or the platform in the current context/i;

/** Expected business / client outcomes that should not create Sentry issues. */
const SENTRY_EXPECTED_BUSINESS_PATTERNS: RegExp[] = [
  /Selfie is not passed check/i,
  /HyperVerge liveness check failed/i,
  /error_no_camera/i,
  /error_no_camera_access/i,
  /Requested device not found/i,
];

const SENTRY_JUNK_STACK_FRAME_PATTERNS: RegExp[] = [
  /checkPerfReady/i,
  /navigationPerformanceLoggerWithReply/i,
  // Browser extensions (ad blockers, privacy tools) patching window.fetch
  /(?:chrome|moz|safari-web)-extension:\/\//i,
  /\/ag-sdk\.js/i,
];

const normalizeEndpointPath = (url?: string): string => {
  if (!url) {
    return UNKNOWN_HTTP_VALUE;
  }

  let endpointPath: string;

  try {
    endpointPath = new URL(url, 'https://sentry.local').pathname;
  } catch {
    endpointPath = url.split(/[?#]/u)[0];
  }

  const normalizedSegments = endpointPath.split('/').map(segment => {
    if (NUMERIC_PATH_SEGMENT_PATTERN.test(segment) || UUID_PATH_SEGMENT_PATTERN.test(segment)) {
      return ':id';
    }

    return segment;
  });

  return normalizedSegments.join('/') || '/';
};

const isAxiosErrorLike = (error: unknown): error is AxiosErrorLike => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return ('isAxiosError' in error && error.isAxiosError === true) || ('name' in error && error.name === 'AxiosError');
};

const getAxiosErrorSentryContext = (hint: EventHint): AxiosErrorSentryContext | null => {
  if (!isAxiosErrorLike(hint.originalException)) {
    return null;
  }

  const { config, response } = hint.originalException;

  return {
    endpoint: normalizeEndpointPath(config?.url),
    method: config?.method?.toUpperCase() ?? UNKNOWN_HTTP_VALUE.toUpperCase(),
    statusCode: response?.status?.toString() ?? UNKNOWN_HTTP_VALUE,
  };
};

const applyAxiosErrorGrouping = (event: ErrorEvent, hint: EventHint): ErrorEvent => {
  const axiosContext = getAxiosErrorSentryContext(hint);

  if (!axiosContext) {
    return event;
  }

  const { endpoint, method, statusCode } = axiosContext;
  const issueTitle = `${method} ${endpoint} [${statusCode}]`;
  const exception = event.exception?.values?.[0];

  if (exception) {
    exception.value = issueTitle;
  } else {
    event.message = issueTitle;
  }

  event.fingerprint = ['axios-http-error', method, endpoint, statusCode];
  event.tags = {
    ...event.tags,
    'http.endpoint': endpoint,
    'http.method': method,
    'http.status_code': statusCode,
  };

  return event;
};

const isAxiosNetworkErrorHint = (hint: EventHint): boolean => {
  const originalException = hint.originalException;

  if (!isAxiosErrorLike(originalException)) {
    return false;
  }

  if (originalException.code === 'ERR_NETWORK') {
    return true;
  }

  return (
    typeof originalException.message === 'string' && originalException.message.toLowerCase().includes('network error')
  );
};

const isAxiosCanceledErrorHint = (hint: EventHint): boolean => {
  const originalException = hint.originalException;

  if (!originalException || typeof originalException !== 'object') {
    return false;
  }

  if ('code' in originalException && originalException.code === 'ERR_CANCELED') {
    return true;
  }

  if ('name' in originalException && originalException.name === 'CanceledError') {
    return true;
  }

  return false;
};

const isExpectedAxiosBusinessErrorHint = (hint: EventHint): boolean => {
  const originalException = hint.originalException;

  if (!isAxiosErrorLike(originalException)) {
    return false;
  }

  const status = originalException.response?.status;

  return status === HTTP_TOO_MANY_REQUESTS_STATUS || originalException.response?.data?.detail === WRONG_OTP_DETAIL;
};

const collectSentryErrorText = (event: ErrorEvent, hint: EventHint): string => {
  const exception = event.exception?.values?.[0];
  const originalException = hint.originalException;
  const originalMessage =
    originalException instanceof Error
      ? originalException.message
      : typeof originalException === 'string'
      ? originalException
      : '';

  return [event.message, exception?.type, exception?.value, originalMessage].filter(Boolean).join(' ');
};

const isIgnoredOmniWidgetAudioError = (event: ErrorEvent, hint: EventHint): boolean => {
  const errorText = collectSentryErrorText(event, hint);

  if (!OMNI_WIDGET_AUDIO_NOT_ALLOWED_PATTERN.test(errorText)) {
    return false;
  }

  const frames = event.exception?.values?.flatMap(value => value.stacktrace?.frames ?? []) ?? [];

  return frames.some(frame => {
    const frameSource = [frame.filename, frame.abs_path].filter(Boolean).join(' ');
    return OMNI_WIDGET_FILE_PATTERN.test(frameSource);
  });
};

const hasJunkStackFrame = (event: ErrorEvent): boolean => {
  const frames = event.exception?.values?.flatMap(value => value.stacktrace?.frames ?? []) ?? [];

  return frames.some(frame => {
    const frameText = [frame.function, frame.filename, frame.module, frame.abs_path].filter(Boolean).join(' ');

    return SENTRY_JUNK_STACK_FRAME_PATTERNS.some(pattern => {
      return pattern.test(frameText);
    });
  });
};

const shouldDropSentryErrorEvent = (event: ErrorEvent, hint: EventHint): boolean => {
  if (isAxiosNetworkErrorHint(hint) || isAxiosCanceledErrorHint(hint) || isExpectedAxiosBusinessErrorHint(hint)) {
    return true;
  }

  if (event.extra?.status === HTTP_TOO_MANY_REQUESTS_STATUS) {
    return true;
  }

  if (isIgnoredOmniWidgetAudioError(event, hint)) {
    return true;
  }

  const errorText = collectSentryErrorText(event, hint);

  if (SENTRY_WEBVIEW_JUNK_PATTERNS.some(pattern => pattern.test(errorText))) {
    return true;
  }

  if (SENTRY_CLIENT_NETWORK_NOISE_PATTERNS.some(pattern => pattern.test(errorText))) {
    return true;
  }

  if (SENTRY_HTTP_429_PATTERNS.some(pattern => pattern.test(errorText))) {
    return true;
  }

  if (SENTRY_EXPECTED_BUSINESS_PATTERNS.some(pattern => pattern.test(errorText))) {
    return true;
  }

  return hasJunkStackFrame(event);
};

export const sentryBeforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  if (shouldDropSentryErrorEvent(event, hint)) {
    return null;
  }

  return applyAxiosErrorGrouping(event, hint);
};

const TAG_USER_UUID = 'context.user_uuid';
const TAG_SESSION_UUID = 'context.session_uuid';
const TAG_ADMIN_ID = 'context.admin_id';

interface SyncSentryContextOptions {
  phoneNumber?: string;
}

const getUserUuid = (): string => localStorage.getItem(KEY_USER_UUID) ?? '';
const getSessionUuid = (): string => localStorage.getItem(KEY_SESSION_UUID) ?? '';
const getAdminId = (): string => localStorage.getItem(IMPERSONATE_ADMIN_ID) ?? '';

const unsetTag = (key: string): void => {
  Sentry.setTag(key, undefined);
};

const syncTag = (key: string, value: string): void => {
  if (value) {
    Sentry.setTag(key, value);
    return;
  }

  unsetTag(key);
};

export const clearSentryContext = (): void => {
  Sentry.setUser(null);
  unsetTag(TAG_USER_UUID);
  unsetTag(TAG_SESSION_UUID);
  unsetTag(TAG_ADMIN_ID);
};

export const syncSentryContext = (options: SyncSentryContextOptions = {}): void => {
  const userUuid = getUserUuid();
  const sessionUuid = getSessionUuid();
  const adminId = getAdminId();

  if (!userUuid && !sessionUuid && !adminId) {
    clearSentryContext();
    return;
  }

  syncTag(TAG_USER_UUID, userUuid);
  syncTag(TAG_SESSION_UUID, sessionUuid);
  syncTag(TAG_ADMIN_ID, adminId);

  if (userUuid) {
    Sentry.setUser({
      id: userUuid,
      ...(options.phoneNumber ? { phoneNumber: options.phoneNumber } : {}),
    });
  } else {
    Sentry.setUser(null);
  }
};
